import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '@/i18n/languages';

/**
 * Production origin. It lives here rather than in `Seo.tsx` because everything
 * that needs to build an absolute URL — the head tags, the sitemap, llms.txt,
 * the prerender — reads this registry, and `Seo.tsx` would otherwise be both a
 * consumer of this module and the owner of a constant it imports back.
 * `Seo.tsx` re-exports it, so its existing importers are unaffected.
 */
export const SITE_URL = 'https://aikka.ai';

/**
 * THE list of publicly crawlable routes — one registry, every consumer.
 *
 * The route list used to be written down twice — once as `<Route path>` in
 * `App.tsx`, once as `<loc>` in a hand-typed `public/sitemap.xml` — and the two
 * had already drifted: `/store` was routed and missing from the sitemap. Two
 * hand-maintained lists always diverge, so this module is the single source the
 * rest of the SEO work reads: the `hreflang` alternates emitted by `<Seo>`, and
 * — since SEO-4 — `public/sitemap.xml` and `public/llms.txt`, which are now
 * RENDERED from this array by `scripts/generate-seo-files.mjs`. Since SEO-5 it
 * also drives the build-time prerender: `scripts/prerender.mjs` writes one
 * static `dist/<route>/index.html` per entry below, and
 * `components/seo/prerender/entry-server.tsx` fails the build if it holds a
 * page component for a path this array does not list, or lacks one for a path
 * it does.
 *
 * WHAT IS NOT HERE, deliberately: per-route titles and descriptions. Every
 * public page already owns its title/description and passes them to `<Seo>`
 * (several build them from the i18n catalogue with `t()`, so they are not
 * static strings at all). Copying them here would create exactly the second
 * list this module exists to abolish — and would freeze a French string into
 * a module that cannot translate it. `llms.txt` needs those sentences, and the
 * generator reads them back OUT of the pages' own `<Seo>` props rather than
 * asking anyone to restate them here.
 *
 * A route earns a place here when it is reachable signed-out AND we want it in
 * the index. Auth screens, `/s/:shareId` share links, the Stripe billing
 * callbacks and `/tier-demo` are reachable signed-out but are `Disallow`ed in
 * `robots.txt`, so they stay out.
 */
export interface PublicRoute {
  /** Path as declared in `App.tsx`, always starting with '/'. */
  path: string;
  /** sitemap.xml <changefreq>. */
  changefreq: 'weekly' | 'monthly' | 'yearly';
  /** sitemap.xml <priority>. */
  priority: number;
}

/**
 * Paths and priorities were taken verbatim from the sitemap the site already
 * published — nothing here is invented. The one addition is `/store`: a real
 * route in `App.tsx` that the hand-typed sitemap had forgotten.
 *
 * This array is now the ONLY place they are written: `npm run seo:generate`
 * renders `public/sitemap.xml` and `public/llms.txt` from it, and
 * `npm run lint` fails when the published files no longer match. Adding a
 * public route is therefore two steps — route it in `App.tsx`, list it here —
 * and the generator refuses to run if a page mounts an indexable `<Seo>` for a
 * path this list does not know.
 */
export const PUBLIC_ROUTES: readonly PublicRoute[] = [
  { path: '/', changefreq: 'weekly', priority: 1.0 },
  { path: '/pharmageo', changefreq: 'weekly', priority: 0.9 },
  { path: '/why-aikka', changefreq: 'monthly', priority: 0.8 },
  { path: '/store', changefreq: 'monthly', priority: 0.6 },
  { path: '/community', changefreq: 'monthly', priority: 0.6 },
  { path: '/resources/security', changefreq: 'monthly', priority: 0.6 },
  { path: '/resources/support', changefreq: 'monthly', priority: 0.6 },
  { path: '/resources/docs', changefreq: 'monthly', priority: 0.7 },
  { path: '/resources/changelog', changefreq: 'monthly', priority: 0.5 },
  { path: '/products/kol', changefreq: 'monthly', priority: 0.7 },
  { path: '/products/medical-watch', changefreq: 'monthly', priority: 0.7 },
  { path: '/products/persona', changefreq: 'monthly', priority: 0.7 },
  { path: '/products/market-access', changefreq: 'monthly', priority: 0.7 },
  // L'index des pages annexes. Priorité au-dessus des documents eux-mêmes :
  // c'est la page que le pied de page de la home désigne (« Resources »), donc
  // la porte d'entrée de la famille.
  // LES SEPT DOCUMENTS SONT SOUS `/resources` DEPUIS LA FUSION, et le sitemap
  // ne publie QUE ces adresses-là. Les anciennes (/legal, /privacy, /terms,
  // /cookies, /security et les deux sous-pages) répondent toujours, mais en
  // redirection (App.tsx) : les publier ici en ferait des doublons que le
  // crawler devrait départager lui-même.
  { path: '/resources', changefreq: 'monthly', priority: 0.4 },
  // Le lead magnet de septembre 2026, au-dessus des documents légaux.
  { path: '/resources/pharma-geo-playbook-2026', changefreq: 'monthly', priority: 0.6 },
  { path: '/resources/legal', changefreq: 'yearly', priority: 0.3 },
  { path: '/resources/privacy', changefreq: 'yearly', priority: 0.3 },
  // Les deux sous-pages de confidentialité — elles remontent des sections de
  // /privacy, donc même fréquence et une priorité d'un cran en dessous.
  { path: '/resources/privacy/data-protection', changefreq: 'yearly', priority: 0.2 },
  { path: '/resources/privacy/data-use', changefreq: 'yearly', priority: 0.2 },
  { path: '/resources/terms', changefreq: 'yearly', priority: 0.3 },
  { path: '/resources/cookies', changefreq: 'yearly', priority: 0.3 },
];

/**
 * The query parameter that pins a language on a URL.
 *
 * WHY A QUERY PARAMETER AND NOT `/en/pricing`.
 * The site serves fr / en / es from the *same* URL: the language is picked
 * client-side by i18next's LanguageDetector and cached in localStorage. With
 * one URL per three languages, `hreflang` has literally nothing to point at.
 * Two ways out:
 *
 *   (A) an indexable language parameter — `?lang=en` — recognised at boot by
 *       the detector (`src/i18n/index.ts`, `order` starts with 'querystring').
 *   (B) a route prefix — `/en/pricing` — the canonical answer, and the one
 *       Google documents first.
 *
 * (B) is better SEO and it is not what this item does: it means re-routing all
 * of `App.tsx`, teaching every internal `<Link>` to carry the prefix, and
 * redirecting the existing unprefixed URLs — a change far wider than the SEO
 * lot, and one that would collide with the chrome/navigation work running in
 * parallel. (A) costs two lines in the detector, creates no route, and the
 * prerender step can emit one file per (route × language) later without any
 * further routing change. So: (A), knowingly, and this comment is the record.
 *
 * The load-bearing half is that `?lang=` is *honoured*. An `hreflang` pointing
 * at a URL that renders the same language as every other URL is a lie told to
 * a crawler, and it is why this constant and the detector config must move
 * together.
 */
export const LANG_QUERY_PARAM = 'lang';

/**
 * Open Graph wants `language_TERRITORY`, i18next speaks bare ISO 639-1.
 *
 * The territory is not a guess: it is the flag the language switcher already
 * shows for each locale in `src/i18n/languages.ts` (🇫🇷 / 🇬🇧 / 🇪🇸). Keys are
 * exactly the codes in `SUPPORTED_LANGUAGES`; `publicRoutes.test.ts` fails if
 * a language is added to that list without a locale here.
 */
export const OG_LOCALES: Readonly<Record<string, string>> = {
  fr: 'fr_FR',
  en: 'en_GB',
  es: 'es_ES',
};

export function ogLocale(code: string): string {
  // `supportedLngs` in `src/i18n/index.ts` restricts the resolved language to
  // the codes above, so both fallbacks are unreachable in practice — they only
  // exist so an unknown code degrades to the default language's locale rather
  // than writing `undefined` into an `og:locale`.
  return OG_LOCALES[code] ?? OG_LOCALES[DEFAULT_LANGUAGE] ?? DEFAULT_LANGUAGE;
}

export interface HreflangAlternate {
  /** hreflang value: an ISO code, or 'x-default'. */
  hreflang: string;
  /** Absolute URL. */
  href: string;
}

/** Absolute URL for `path` pinned to `code` via the language parameter. */
export function localizedUrl(path: string, code: string): string {
  return `${SITE_URL}${path}?${LANG_QUERY_PARAM}=${code}`;
}

/**
 * The full `hreflang` cluster for one path: the three languages plus x-default.
 *
 * `x-default` is the BARE url (`https://aikka.ai/pricing`), and that is a
 * deliberate reading of the spec rather than a shortcut. x-default means "the
 * page for a visitor whose language none of the others target" — canonically a
 * URL that auto-detects. The bare URL is precisely that: it carries no `?lang=`
 * so the detector falls through to localStorage, then the browser's own
 * `Accept-Language`, then the `fr` fallback.
 *
 * The alternative — declaring the bare URL as `hreflang="fr"` — would be one
 * URL shorter, and it would be false: a first-time visitor with an English
 * browser lands on the bare URL and gets English. Announcing that URL to Google
 * as the French page is the kind of small lie this repo does not tell. So `fr`
 * gets its own explicit `?lang=fr`, like the other two.
 *
 * The bare URL has to be IN the cluster whatever we do: it is what the sitemap
 * lists and what every inbound link points at, and Google drops an hreflang set
 * that does not include the page it is reading.
 */
export function hreflangAlternates(path: string): HreflangAlternate[] {
  return [
    ...SUPPORTED_LANGUAGES.map((l) => ({ hreflang: l.code, href: localizedUrl(path, l.code) })),
    { hreflang: 'x-default', href: `${SITE_URL}${path}` },
  ];
}
